import React from 'react'

// Organization icons
import { HomeFilled, QuestionCircleFilled, CopyFilled, SmileFilled, IdcardFilled, FileTextFilled, CreditCardFilled } from '@ant-design/icons'
// Integration icons
import { ApiFilled, CodeFilled } from '@ant-design/icons'
// Administration icons
import { ProjectFilled, TeamOutlined, UserOutlined, ApartmentOutlined } from '@ant-design/icons'
import { SpecieIcon, BreedIcon, GroupSpecieIcon } from '../components/Icons'

// Available views
import Auth from '../views/Auth'
import Question from '../views/Question'
import QuestionTemplate from '../views/QuestionTemplate'
import Shelter from '../views/Shelter'
import Token from '../views/Token'
import Mapping from '../views/Mapping'
import AdoptionApplication from '../views/AdoptionApplication'
import AdoptionPetSetAvailable from '../views/AdoptionPetSetAvailable'
import AdminRole from '../views/AdminRole'
import AdminUser from '../views/AdminUser'
import Specie from '../views/Specie'
import Announcement from '../views/Announcement'
import Breed from '../views/Breed'
import Group from '../views/Group'
// import Sponsor from '../views/Sponsor'
//import SmartResponse from '../views/SmartResponse'
import Integration from '../views/Integration'
import IntegrationNetwork from '../views/IntegrationNetwork'
import IntegrationDefinitions from '../views/IntegrationDefinitions'
import Invoices from 'views/Financial'
import Credits from 'views/Financial/credits'
import AdoptionDeleteAdopter from 'views/AdoptionDeleteAdopter'

export type MenuGroupType = {
  name: string,
  icon: React.ReactNode,
  items: Array<MenuItemType>
}

export type MenuItemType = {
  path: string,
  name: string,
  icon: React.ReactNode
}

export type AppRouteType = {
  path: string | Array<string>,
  exact: boolean,
  isPrivate: boolean,
  name: string,
  permissions?: AppRoutePermissionType,
  component: any
}

export type AppRoutePermissionType = {
  list: string | AppRoutePermissionTabType[],
  create?: string | AppRoutePermissionTabType[], 
  save?: string | AppRoutePermissionTabType[],
  delete?: string | AppRoutePermissionTabType[]
}

export type AppRoutePermissionTabType = {
  activeTab: string | null,
  permission: string[]
}

/**
 * Routes
 */
export default [
  {
    path: '/login',
    exact: true,
    isPrivate: false,
    name: 'Authentication',
    component: Auth
  },
  {
    path: '/organization/question/:questionUuid?',
    exact: true,
    isPrivate: true,
    name: 'Questions',
    permissions: {
      list: '/admin/question/search',
      create: '/admin/question/save',
      save: '/admin/question/save'
    },
    component: Question
  },
  {
    path: '/organization/question-template/:templateUuid?',
    exact: true,
    isPrivate: true,
    name: 'Question Templates',
    permissions: {
      list: '/admin/question-template/search',
      create: '/admin/question-template/save',
      save: '/admin/question-template/save'
    },
    component: QuestionTemplate
  },
  {
    path: '/organization/shelter/:shelterUuid?/:activeTab?/:entityUuid?',
    exact: true,
    isPrivate: true,
    name: 'Shelters',
    permissions: {
      list: [
        {
          activeTab: null,
          permission: ['/admin/shelter/search']
        },
        {
          activeTab: 'general',
          permission: ['/admin/shelter/search', '/admin/shelter-settings/search']
        },
        {
          activeTab: 'question',
          permission: ['/admin/question/search', '/admin/question-template/search']
        }
      ],
      create: '/admin/shelter/create',
      save: [
        {
          activeTab: 'general',
          permission: ['/admin/shelter/save', '/admin/shelter-settings/save-adoption']
        },
        {
          activeTab: 'question',
          permission: ['/admin/shelter/update-question']
        },
        {
          activeTab: 'pet-restore',
          permission: ['/admin/pet/restore-pet']
        },
        {
          activeTab: 'product',
          permission: ['/admin/product/search', '/admin/product/create', '/admin/product/update', '/admin/product/delete'],
        },
        {
          activeTab: 'tax',
          permission: ['/admin/tax/search', '/admin/tax/create', '/admin/tax/update', '/admin/tax/delete'],
        },
        {
          activeTab: 'iframe',
          permission: ['/admin/shelter-settings/save-iframe']
        },
        {
          activeTab: 'kennel-card',
          permission: ['/admin/shelter-settings/save-kennel-card']
        },
        {
          activeTab: 'pet-characteristics',
          permission: ['admin/shelter-settings/save-pet-characteristics-alias']
        }
      ]
    },
    component: Shelter
  },
  {
    path: '/organization/token/:tokenUuid?',
    exact: true,
    isPrivate: true,
    name: 'Tokens',
    permissions: {
      list: '/admin/token/search',
      create: '/admin/token/generate',
      save: '/admin/token/generate'
    },
    component: Token
  },
  {
    path: '/organization/adoption-application/:applicationUuid?/:activeTab?',
    exact: true,
    isPrivate: true,
    name: 'Adoption Application',
    permissions: {
      list: [
        {
          activeTab: null,
          permission: ['/admin/application/search'] 
        },
        {
          activeTab: 'checkout',
          permission: ['/admin/application/search']
        }
      ],
      save: [
        {
          activeTab: 'checkout',
          permission: ['/admin/application/checkout-update']
        }
      ],
      delete: [
        {
          activeTab: 'checkout',
          permission: ['/admin/application/remove-checkout-product']
        }
      ]
    },
    component: AdoptionApplication
  },
  {
    path: '/organization/adoption-setapproved',
    exact: true,
    isPrivate: true,
    name: 'Pets Stuck on Approved',
    permissions: {
      list: [
        {
          activeTab: null,
          permission: ['/admin/application/search'] 
        },
        {
          activeTab: 'checkout',
          permission: ['/admin/application/search']
        }
      ],
      save: [
        {
          activeTab: 'checkout',
          permission: ['/admin/application/checkout-update']
        }
      ],
      delete: [
        {
          activeTab: 'checkout',
          permission: ['/admin/application/remove-checkout-product']
        }
      ]
    },
    component: AdoptionPetSetAvailable
  },
  {
    path: '/integration-mapping/:activeTab?/:entityUuid?',
    exact: true,
    isPrivate: true,
    name: 'Mappings',
    permissions: {
      list: [
        {
          activeTab: 'age',
          permission: ['/admin/mapping-age/search']
        },
        {
          activeTab: 'age-range',
          permission: ['/admin/mapping-age-range/search']
        },
        {
          activeTab: 'breed',
          permission: ['/admin/mapping-breed/search']
        },
        {
          activeTab: 'sex',
          permission: ['/admin/mapping-sex/search']
        },
        {
          activeTab: 'size',
          permission: ['/admin/mapping-size/search']
        },
        {
          activeTab: 'specie',
          permission: ['/admin/mapping-specie/search']
        }
      ],
      create: [
        {
          activeTab: 'age',
          permission: ['/admin/mapping-age/save']
        },
        {
          activeTab: 'age-range',
          permission: ['/admin/mapping-age-range/save']
        },
        {
          activeTab: 'breed',
          permission: ['/admin/mapping-breed/save']
        },
        {
          activeTab: 'sex',
          permission: ['/admin/mapping-sex/save']
        },
        {
          activeTab: 'size',
          permission: ['/admin/mapping-size/save']
        },
        {
          activeTab: 'specie',
          permission: ['/admin/mapping-specie/save']
        }
      ],
      save: [
        {
          activeTab: 'age',
          permission: ['/admin/mapping-age/save']
        },
        {
          activeTab: 'age-range',
          permission: ['/admin/mapping-age-range/save']
        },
        {
          activeTab: 'breed',
          permission: ['/admin/mapping-breed/save']
        },
        {
          activeTab: 'sex',
          permission: ['/admin/mapping-sex/save']
        },
        {
          activeTab: 'size',
          permission: ['/admin/mapping-size/save']
        },
        {
          activeTab: 'specie',
          permission: ['/admin/mapping-specie/save']
        }
      ]
    },
    component: Mapping
  },
  {
    path: '/integration/:integrationUuid?/:activeTab?',
    exact: true,
    isPrivate: true,
    name: 'Integrations',
    permissions: {
      list: '/admin/integration/search'
    },
    component: Integration
  },{
    path: '/network/:integrationUuid?/:activeTab?',
    exact: true,
    isPrivate: true,
    name: 'Network',
    permissions: {
      list: '/admin/integration/search'
    },
    component: IntegrationNetwork
  },{
    path: '/definitions/:integrationUuid?/:activeTab?',
    exact: true,
    isPrivate: true,
    name: 'Definitions',
    permissions: {
      list: '/admin/integration/search'
    },
    component: IntegrationDefinitions
  },
  {
    path: '/admin/role/:roleName?',
    exact: true,
    isPrivate: true,
    name: 'Admin Role',
    permissions: {
      list: '/admin/role/list',
      create: '/admin/role/create',
      save: '/admin/role/update',
      delete: '/admin/role/delete'
    },
    component: AdminRole
  },
  {
    path: '/admin/delete-adopter',
    exact: true,
    isPrivate: true,
    name: 'Admin Role',
    permissions: {
      delete: '/admin/adopter-user/delete-account'
    },
    component: AdoptionDeleteAdopter
  },
  {
    path: '/admin/user/:userUuid?',
    exact: true,
    isPrivate: true,
    name: 'Admin Users',
    permissions: {
      list: '/admin/user/search',
      create: '/admin/user/create',
      save: '/admin/user/update',
      delete: '/admin/user/delete'
    },
    component: AdminUser
  },
  {
    path: '/admin/specie/:specieUuid?',
    exact: true,
    isPrivate: true,
    name: 'Species',
    permissions: {
      list: '/admin/specie/search',
      create: '/admin/specie/create',
      save: '/admin/specie/update',
      delete: '/admin/specie/delete'
    },
    component: Specie
  },
  {
    path: '/admin/announcement/:announcementUuid?',
    exact: true,
    isPrivate: true,
    name: 'Announcements',
    permissions: {
      list: '/admin/announcement/search',
      create: '/admin/announcement/create',
      save: '/admin/announcement/update',
      delete: '/admin/announcement/turn-off'
    },
    component: Announcement
  },
  {
    path: '/admin/breed/:breedUuid?',
    exact: true,
    isPrivate: true,
    name: 'Breeds',
    permissions: {
      list: '/admin/breed/search',
      create: '/admin/breed/create',
      save: '/admin/breed/update',
      delete: '/admin/breed/delete'
    },
    component: Breed
  },
  {
    path: '/admin/group/:groupUuid?',
    exact: true,
    isPrivate: true,
    name: 'Groups',
    permissions: {
      list: '/admin/group/search',
      create: '/admin/group/create',
      save: '/admin/group/update',
      delete: '/admin/group/delete'
    },
    component: Group
  },
  {
    path: '/financial/invoices',
    exact: true,
    isPrivate: true,
    name: 'Cycles',
    component: Invoices,
    permissions: {
      list: '/admin/payment-cycle/search',
    }
  },
  {
    path: '/financial/credits/:creditUuid?',
    exact: true,
    isPrivate: true,
    name: 'Credits',
    component: Credits,
    permissions: {
      list: '/admin/payment-credit/search',
      create: '/admin/payment-credit/create-voucher',
    }
  },
  
  /*{
    path: '/organization/event/:eventUuid?',
    exact: true,
    isPrivate: true,
    name: 'Events',
    permissions: {
      list: '/admin/event/search',
      create: '/admin/event/create',
      save: '/admin/event/update',
      delete: '/admin/event/delete'
    },
    component: Event
  },*/
  /*{
    path: '/organization/smart-response/:templateUuid?',
    exact: true,
    isPrivate: true,
    name: 'Smart Response',
    permissions: {
      list: '/admin/email-template/search',
      create: '/admin/email-template/create',
      save: '/admin/email-template/update',
      delete: '/admin/email-template/delete'
    },
    component: SmartResponse
  }*/
] as Array<AppRouteType>

/**
 * Menu items
 */
export const MenuItems: Array<MenuGroupType | MenuItemType> = [
  {
    name: 'Organization',
    icon: <HomeFilled />,
    items: [
      {
        path: '/organization/adoption-application',
        name: 'Adoptions',
        icon: <FileTextFilled />
      },
      {
        path: '/organization/adoption-setapproved',
        name: 'Pets Stuck on Approved ',
        icon: <FileTextFilled />
      },
      {
        path: '/organization/question',
        name: 'Questions',
        icon: <QuestionCircleFilled />
      },
      {
        path: '/organization/question-template',
        name: 'Question Templates',
        icon: <CopyFilled />
      },
      {
        path: '/organization/shelter',
        name: 'Shelters',
        icon: <SmileFilled />
      },
      // {
      //   path: '/organization/sponsor',
      //   name: 'Sponsors',
      //   icon: <TagsFilled />
      // },
      /*{
        path: '/organization/event',
        name: 'Events',
        icon: <CalendarFilled />
      },*/
      /*{
        path: '/organization/smart-response',
        name: 'Smart Response',
        icon: <MessageFilled />
      },*/
      {
        path: '/organization/token',
        name: 'Tokens',
        icon: <IdcardFilled />
      }
    ]
  },
  {
    name: 'Integration',
    icon: <ApiFilled />,
    items: [
      {
        path: '/integration',
        name: 'Actives',
        icon: <ApartmentOutlined />
      },
      {
        path: '/integration-mapping/age',
        name: 'Mappings',
        icon: <CodeFilled />
      },{
        path: '/network',
        name: 'Network',
        icon: <ApartmentOutlined />
      },{
        path: '/definitions',
        name: 'Definitions',
        icon: <ApartmentOutlined />
      },
    ]
  },
  {
    name: 'Financial',
    icon: <CreditCardFilled />,
    items: [
      {
        path: '/financial/invoices',
        name: 'Cycles',
        icon: <CreditCardFilled />
      },
      {
        path: '/financial/credits',
        name: 'Credits',
        icon: <CreditCardFilled />
      }
    ]
  },
  {
    name: 'Administration',
    icon: <ProjectFilled />,
    items: [
      {
        path: '/admin/role',
        name: 'Roles',
        icon: <TeamOutlined />
      },
      {
        path: '/admin/user',
        name: 'Users',
        icon: <UserOutlined />
      },
      {
        path: '/admin/delete-adopter',
        name: 'Delete Adopter’s Account',
        icon: <UserOutlined />
      },
      {
        path: '/admin/specie',
        name: 'Species',
        icon: <SpecieIcon />
      },
      {
        path: '/admin/breed',
        name: 'Breeds',
        icon: <BreedIcon />
      },
      {
        path: '/admin/group',
        name: 'Groups',
        icon: <GroupSpecieIcon />
      },
      {
        path: '/admin/announcement',
        name: 'Announcements',
        icon: <FileTextFilled />
      }
    ]
  }
]
